import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import UserContext from "./hooks/UserContext";
import Indicators from "./pages/Indicators";
import NoPage from "./pages/NoPage";
import Filial from "./pages/Filial";
import Substations from "./pages/Substations";
import Networks from "./pages/Networks";
import Home from "./pages/Home";
import PassportColumn from "./pages/PassportColumn";
import Passport from "./pages/Passport";
import Form from "./pages/Form";
import Login from "./pages/Login";
import Map from "./pages/Map";
import Application from "./pages/Application";
import ApplicationManagement from "./pages/ApplicationManagement";
import SubstationsOverview from "./pages/SubstationsOverview";
import AddReadings from "./pages/AddReadings";
import Dashboard from "./pages/Dashboard";
import Poles from "./pages/Poles";
import NetPassport from "./pages/NetPassport";
import SubPassport from "./pages/SubPassport";
import SubstationDetails from "./pages/SubstationDetails";
import AddAccident from "./pages/AddAccident";
import Accidents from "./pages/Accidents";
import Turnstile from "./pages/Turnstile";
import FaceEnroll from "./pages/FaceEnroll";
import AccidentAddInfo from "./pages/AccidentAddInfo";
import ProtectedRoute from "./components/protectedRoute/ProtectedRoute"
import NoAccess from "./pages/NoAccess";
import apiFetch from "./utils/apiFetch";
import usePushNotifications from "./hooks/usePushNotifications";
import TurnstileLogin from "./pages/TurnstileLogin";
import AddPoleCheckup from "./pages/AddPoleCheckup";
import CheckupsPage from "./pages/Checkups";
import AdminUsers from "./pages/AdminUsers";
import CheckupsNew from "./pages/CheckupsNew";
import Monitoring from "./pages/Monitoring";
import IncidentDetail from "./pages/IncidentDetail";
import MonitoringStats from "./pages/MonitoringStats";
import CentralDashboard from "./pages/CentralDashboard";
import SubstationList from "./pages/SubstationList";
import SubstationCreate from "./pages/SubstationCreate";
import SubstationDetailPage from "./pages/SubstationDetail";
import NetworkList from "./pages/NetworkList";
import NetworkCreate from "./pages/NetworkCreate";
import NetworkDetail from "./pages/NetworkDetail";
import EquipmentPassport from "./pages/EquipmentPassport";
import PublicEquipmentPassport from "./pages/PublicEquipmentPassport";
import StatisticsEquipment from "./pages/statistics/Equipment";
import StatisticsNetworks from "./pages/statistics/Networks";
import StatisticsActivity from "./pages/statistics/Activity";
import StatisticsEmployees from "./pages/statistics/Employees";
import StatisticsAccidents from "./pages/statistics/Accidents";

function App() {
  const [user, setUser] = useState(null);
  usePushNotifications(user);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isSuper, setIsSuper] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  
  const login = (userData) => {
    setUser(userData);
    // localStorage.setItem("token", userData.token);

    // Удаляем токен через 24 часа
    setTimeout(() => {
      localStorage.removeItem("token");
      setUser(null);
    }, 24 * 60 * 60 * 1000); // 24 часа в миллисекундах
  };

  const logout = () => {
    try { localStorage.removeItem("token"); } catch (_) {}
    setUser(null);
    setRoles([]);
    setPermissions([]);
    setIsSuper(false);
  };

  const checkUser = async ({ requireFresh = false } = {}) => {
    setAuthLoading(true);
    try {
      const res = await apiFetch("/api/auth/me");
      if (!res.ok) {
        const error = new Error("Profile request failed");
        error.status = res.status;
        error.retryAfter = res.headers.get("Retry-After");
        throw error;
      }
      const data = await res.json();
      if (!data?.user?.id) {
        const error = new Error("Invalid profile response");
        error.code = "INVALID_AUTH_RESPONSE";
        throw error;
      }
      setUser(data.user);
      setRoles(data.roles || []);
      setPermissions(data.permissions || []);
      setIsSuper(Boolean(data.isSuper));
      // Кешируем данные для офлайн-режима
      try {
        localStorage.setItem("_uc", JSON.stringify({
          user: data.user,
          roles: data.roles || [],
          permissions: data.permissions || [],
          isSuper: Boolean(data.isSuper),
        }));
      } catch (_) {
        // Отсутствие места для офлайн-кеша не мешает входу с проверенным токеном.
        // Старый кеш может принадлежать другому пользователю.
        try { localStorage.removeItem("_uc"); } catch (_) {}
      }
      return data.user;
    } catch (e) {
      if (requireFresh) {
        logout();
        throw e;
      }
      // Если это НЕ ошибка авторизации (401/403) — сеть недоступна или сервер упал
      // Не делаем logout — загружаем кешированные данные если есть
      const isAuthError = e?.status === 401 || e?.status === 403;
      if (!isAuthError && localStorage.getItem("token")) {
        try {
          const cached = JSON.parse(localStorage.getItem("_uc") || "null");
          if (cached?.user) {
            setUser(cached.user);
            setRoles(cached.roles || []);
            setPermissions(cached.permissions || []);
            setIsSuper(Boolean(cached.isSuper));
            return;
          }
        } catch (_) {}
      }
      logout();
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    checkUser();
  }, []); // Пустой массив зависимостей - проверка при монтировании компонента

  return (
    <UserContext.Provider value={{ user, roles, permissions, isSuper, authLoading, checkUser, login, logout }}>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login setAuth={login} />} />
            <Route path="/public/:slug" element={<PublicEquipmentPassport />} />
            <Route path="/p/:publicId" element={<PublicEquipmentPassport />} />
            <Route path="/turnstile-login" element={<TurnstileLogin />} />
            <Route path="/" element={user || localStorage.getItem("token") ? <Indicators /> : <Navigate to="/login" />} />
            <Route path="/no-access" element={user || localStorage.getItem("token") ? <NoAccess /> : <Navigate to="/login" />} />
            <Route path="/statistics" element={<ProtectedRoute><Navigate to="/statistics/equipment" replace /></ProtectedRoute>} />
            <Route path="/statistics/equipment" element={<ProtectedRoute><StatisticsEquipment /></ProtectedRoute>} />
            <Route path="/statistics/networks" element={<ProtectedRoute><StatisticsNetworks /></ProtectedRoute>} />
            <Route path="/statistics/activity" element={<ProtectedRoute><StatisticsActivity /></ProtectedRoute>} />
            <Route path="/statistics/employees" element={<ProtectedRoute><StatisticsEmployees /></ProtectedRoute>} />
            <Route path="/statistics/accidents" element={<ProtectedRoute><StatisticsAccidents /></ProtectedRoute>} />
            <Route path="/home" element={user || localStorage.getItem("token") ? <Home /> : <Navigate to="/login" />} />
            <Route path="/form" element={user || localStorage.getItem("token") ? <Form /> : <Navigate to="/login" />} />
            <Route path="/dashboard" element={user || localStorage.getItem("token") ? <Dashboard /> : <Navigate to="/login" />} />
            <Route path="/dashboard/overview" element={user || localStorage.getItem("token") ? <SubstationsOverview /> : <Navigate to="/login" />} />
            <Route path="/dashboard/add-readings" element={user || localStorage.getItem("token") ? <AddReadings /> : <Navigate to="/login" />} />
            <Route path="/application" element={user || localStorage.getItem("token") ? <Application /> : <Navigate to="/login" />} />
            <Route path="/application/management" element={user || localStorage.getItem("token") ? <ApplicationManagement /> : <Navigate to="/login" />} />
            <Route path="/map" element={user || localStorage.getItem("token") ? <Map /> : <Navigate to="/login" />} />
            <Route path="*" element={<NoPage />} />
            <Route path="/indicators/:slug" element={user || localStorage.getItem("token") ? <Filial /> : <Navigate to="/login" />} />
            <Route path="/indicators/networks/:slug" element={user || localStorage.getItem("token") ? <NetPassport /> : <Navigate to="/login" />} />
            <Route path="/indicators/substations/:slug" element={user || localStorage.getItem("token") ? <SubPassport /> : <Navigate to="/login" />} />
            <Route path="/equipment/:equipmentType/:equipmentId" element={user || localStorage.getItem("token") ? <EquipmentPassport /> : <Navigate to="/login" />} />
            <Route path="/indicators/substations/:slug/:entityType/:id/details" element={user || localStorage.getItem("token") ? <SubstationDetails /> : <Navigate to="/login" />}/>
            {/* <Route path="/indicators/:city/:slug" element={user || localStorage.getItem("token") ? <Passport /> : <Navigate to="/login" />} /> */}
            <Route path="/indicators/networks/:slug/poles/:poleId" element={user || localStorage.getItem("token") ? <Poles /> : <Navigate to="/login" />} />
            {/* <Route path="/indicators/:city/:slug/:id" element={user || localStorage.getItem("token") ? <PassportColumn /> : <Navigate to="/login" />} /> */}
            <Route path="/indicators/substations" element={user || localStorage.getItem("token") ? <Substations /> : <Navigate to="/login" />} />
            <Route path="/indicators/networks" element={user || localStorage.getItem("token") ? <Networks /> : <Navigate to="/login" />} />
            <Route
              path="/substations"
              element={user || localStorage.getItem("token") ? <SubstationList /> : <Navigate to="/login" />}
            />
            <Route
              path="/substations/new"
              element={
                <ProtectedRoute need={["SUBSTATION_CREATE"]}>
                  <SubstationCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/substations/:id"
              element={user || localStorage.getItem("token") ? <SubstationDetailPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/networks"
              element={user || localStorage.getItem("token") ? <NetworkList /> : <Navigate to="/login" />}
            />
            <Route
              path="/networks/new"
              element={
                <ProtectedRoute need={["NETWORK_CREATE"]}>
                  <NetworkCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/networks/:id"
              element={user || localStorage.getItem("token") ? <NetworkDetail /> : <Navigate to="/login" />}
            />
            <Route
              path="/accidents/add"
              element={
                <ProtectedRoute need={["ACCIDENTS_CREATE"]}>
                  <AddAccident />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accidents"
              element={
                <ProtectedRoute need={["ACCIDENTS_READ"]}>
                  <Accidents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accidents/:type/:id/add-info"
              element={
                <ProtectedRoute need={["ACCIDENTS_UPDATE"]}>
                  <AccidentAddInfo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkups/add"
              element={
                <ProtectedRoute need={["POLE_CHECKUP_CREATE"]}>
                  <AddPoleCheckup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkups/new"
              element={
                <ProtectedRoute need={["POLE_CHECKUP_READ"]}>
                  <CheckupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute need={["USER_ADMIN"]}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkups"
              element={
                <ProtectedRoute need={["POLE_CHECKUP_READ"]}>
                  <CheckupsNew />
                </ProtectedRoute>
              }
            />
            {/* <Route path="/turnstile/:deviceId" element={ user || localStorage.getItem("token") ? <Turnstile /> : <Navigate to="/login" />} />
            <Route path="/turnstile/:deviceId/enroll" element={ user || localStorage.getItem("token") ? <FaceEnroll /> : <Navigate to="/login" />} /> */}
            <Route
              path="/monitoring"
              element={
                <ProtectedRoute need={["INCIDENT_READ"]}>
                  <Monitoring />
                </ProtectedRoute>
              }
            />
            <Route
              path="/monitoring/stats"
              element={
                <ProtectedRoute need={["INCIDENT_READ"]}>
                  <MonitoringStats />
                </ProtectedRoute>
              }
            />
            <Route
              path="/monitoring/central"
              element={
                <ProtectedRoute need={["INCIDENT_READ"]}>
                  <CentralDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/monitoring/:id"
              element={
                <ProtectedRoute need={["INCIDENT_READ"]}>
                  <IncidentDetail />
                </ProtectedRoute>
              }
            />
            <Route path="/turnstile/:deviceId" element={<Turnstile />} />
            <Route path="/turnstile/:deviceId/enroll" element={<FaceEnroll />} />
          </Routes>
        </div>
      </Router>
    </UserContext.Provider>
  );
}

export default App;
